// The INVOICEABLE-CHARGE slot — how FIN's "raise an invoice from an unpaid
// library fine" flow works without FIN importing @erp/module-lib.
//
// WHY: Invoices' create panel has a "lib-fine" mode whose picker lists a member's
// un-invoiced unpaid fines. While that screen lived in apps/portals/admin it
// simply called `libraryQueries.fines(...)` — legal, because a portal may import
// any module. Inside modules/fin that is a `module → module` edge (ADR-001
// Fig 4.1): it would fuse the FIN chunk with the LIB chunk and make FIN
// unshippable to an installation with no library.
//
// So FIN names the SHAPE it needs — a charge someone owes, which could as easily
// be a hostel fee or a lab fine — and the shell supplies the source. The default
// yields nothing, and the picker already has an honest empty state ("paste an id
// instead"), so a deployment without a library degrades to manual entry rather
// than breaking.
//
// The source is a FUNCTION the screen calls in its own body, not a ready-made
// list, so the underlying query runs inside the screen's reactive scope and stays
// gated on `enabled` — the panel must not fetch a page of fines on every admin
// page load.
//
// LONGER TERM the vault prefers this composed on the SERVER (placement table:
// a cross-module screen composes data "via IDs + gateway composition"), i.e. FIN's
// api exposing invoiceable charges itself. That is an API change, not a file move,
// so it is tracked separately rather than smuggled into this migration.
import { createContext, useContext } from "solid-js";

/** One outstanding charge a FIN invoice could be raised against. */
export interface InvoiceableCharge {
  id: string;
  /** Who owes it — the picker's first line. */
  payerName?: string | null;
  /** Why it exists — the picker's first line. */
  reason?: string | null;
  amountPaise: number;
  /** Already-invoiced charges are filtered out by the picker. */
  invoiceId?: string | null;
}

export interface InvoiceableChargeList {
  charges: () => InvoiceableCharge[];
  isLoading: () => boolean;
}

/** `enabled` is reactive: the source must not fetch while the panel is closed. */
export type InvoiceableChargeSource = (enabled: () => boolean) => InvoiceableChargeList;

const EMPTY: InvoiceableChargeSource = () => ({ charges: () => [], isLoading: () => false });

const InvoiceableChargesContext = createContext<InvoiceableChargeSource>(EMPTY);

/** The shell wraps its routes in this, passing a source backed by whichever module owns the charges. */
export const InvoiceableChargesProvider = InvoiceableChargesContext.Provider;

/** Call in a component body, then call the result with a reactive `enabled`. */
export const useInvoiceableCharges = () => useContext(InvoiceableChargesContext);
